import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { ShoppingCart, CheckCircle, XCircle, Clock, Pause, Eye, Trash2, RefreshCw, Play, Server, Key, FileText, Send, Wifi, WifiOff, Search, Check, X, ArrowUpDown, Globe } from 'lucide-react';
import { useOrderWebSocket } from '@/hooks/useOrderWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  { value: 'processing', label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: Play },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { value: 'suspended', label: 'Suspended', color: 'bg-orange-100 text-orange-800', icon: Pause },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle }
];

// Valid status transitions for dropdown (Provision is NOT in dropdown - handled by Provision button)
const VALID_TRANSITIONS = {
  'pending': ['processing', 'cancelled'],
  'processing': ['cancelled'],  // Provision handled by button, not dropdown
  'active': ['suspended', 'cancelled'],
  'suspended': ['active', 'cancelled'],
  'cancelled': []
};

export default function AdminOrders() {
  const { canDelete, hasPermission } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [activeAdminTab, setActiveAdminTab] = useState('orders'); // 'orders' | 'service-changes' | 'cancellations' | 'storefront' | 'self-orders'

  // Partner Self-Orders state
  const [selfOrders, setSelfOrders] = useState([]);
  const [selfOrdersLoading, setSelfOrdersLoading] = useState(false);
  const [selfOrderStatusFilter, setSelfOrderStatusFilter] = useState('all');
  const [selfOrderSearch, setSelfOrderSearch] = useState('');
  const [selfOrderProvisionOpen, setSelfOrderProvisionOpen] = useState(false);
  const [selfOrderProvisioningOrder, setSelfOrderProvisioningOrder] = useState(null);
  const [selfOrderProvisionData, setSelfOrderProvisionData] = useState({ server_url: '', port: '', username: '', password: '', bios_ipmi_access: '', admin_notes: '' });
  const [submittingSelfProvision, setSubmittingSelfProvision] = useState(false);
  const [selfOrderProvisionErrors, setSelfOrderProvisionErrors] = useState({});
  const [selfOrderDetailOpen, setSelfOrderDetailOpen] = useState(false);
  const [selfOrderDetail, setSelfOrderDetail] = useState(null);

  // Cancellation Requests state
  const [cancelRequests, setCancelRequests] = useState([]);
  const [cancelReqLoading, setCancelReqLoading] = useState(false);
  const [cancelReviewOpen, setCancelReviewOpen] = useState(false);
  const [selectedCancelReq, setSelectedCancelReq] = useState(null);
  const [cancelReviewAction, setCancelReviewAction] = useState(null); // 'approve' | 'reject'
  const [cancelReviewNotes, setCancelReviewNotes] = useState('');

  // Service Change Requests state
  const [serviceChangeReqs, setServiceChangeReqs] = useState([]);
  const [scrLoading, setScrLoading] = useState(false);
  const [selectedScr, setSelectedScr] = useState(null);
  const [scrDetailOpen, setScrDetailOpen] = useState(false);
  const [scrRejectOpen, setScrRejectOpen] = useState(false);
  const [scrRejectReason, setScrRejectReason] = useState('');
  const [submittingScr, setSubmittingScr] = useState(false);
  const [submittingCancelReview, setSubmittingCancelReview] = useState(false);

  // Storefront Requests state
  const [wlRequests, setWlRequests] = useState([]);
  const [wlReqLoading, setWlReqLoading] = useState(false);
  const [wlUpdating, setWlUpdating] = useState(null);
  const [wlPartnerFilter, setWlPartnerFilter] = useState('all');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [updating, setUpdating] = useState(null);
  
  // Permission checks
  const canEditOrder = hasPermission('orders:edit');
  const canProvisionOrder = hasPermission('orders:provision');
  const canDeleteOrder = hasPermission('orders:delete') && canDelete;
  const canCancelOrder = hasPermission('orders:cancel');
  
  // Confirmation dialog state for suspend/cancel
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState({ orderId: null, newStatus: null });
  
  // Provisioning state
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [provisioningOrder, setProvisioningOrder] = useState(null);
  const [provisioning, setProvisioning] = useState({
    server_url: '',
    port: '',
    username: '',
    password: '',
    bios_ipmi_access: '',
    admin_notes: ''
  });
  const [submittingProvision, setSubmittingProvision] = useState(false);
  const [provisionErrors, setProvisionErrors] = useState({});

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCancelRequests = useCallback(async () => {
    setCancelReqLoading(true);
    try {
      const res = await axios.get(`${API}/admin/cancellation-requests`);
      setCancelRequests(res.data || []);
    } catch {
      // silently fail
    } finally {
      setCancelReqLoading(false);
    }
  }, []);

  const fetchServiceChangeReqs = useCallback(async () => {
    setScrLoading(true);
    try {
      const res = await axios.get(`${API}/parent/service-change-requests`);
      setServiceChangeReqs(res.data || []);
    } catch {
      // silently fail
    } finally {
      setScrLoading(false);
    }
  }, []);

  const fetchWlRequests = useCallback(async () => {
    setWlReqLoading(true);
    try {
      const res = await axios.get(`${API}/admin/white-label/requests`);
      setWlRequests(res.data || []);
    } catch {
      // silently fail
    } finally {
      setWlReqLoading(false);
    }
  }, []);

  const fetchSelfOrders = useCallback(async () => {
    setSelfOrdersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/self-orders`);
      setSelfOrders(res.data || []);
    } catch {
      // silently fail
    } finally {
      setSelfOrdersLoading(false);
    }
  }, []);

  const handleWlStatusChange = async (reqId, newStatus) => {
    setWlUpdating(reqId);
    try {
      await axios.put(`${API}/admin/white-label/requests/${reqId}/status`, { status: newStatus });
      setWlRequests(prev => prev.map(r => r.id === reqId ? { ...r, status: newStatus } : r));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
    finally { setWlUpdating(null); }
  };

  const handleScrApprove = async (scrId) => {
    setSubmittingScr(true);
    try {
      await axios.put(`${API}/parent/service-change-requests/${scrId}/approve`);
      toast.success('Request approved');
      fetchServiceChangeReqs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setSubmittingScr(false);
    }
  };

  const handleScrReject = async () => {
    setSubmittingScr(true);
    try {
      await axios.put(`${API}/parent/service-change-requests/${selectedScr.id}/reject`, { reason: scrRejectReason });
      toast.success('Request rejected');
      setScrRejectOpen(false);
      setScrDetailOpen(false);
      fetchServiceChangeReqs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setSubmittingScr(false);
    }
  };

  const submitCancelReview = async () => {
    setSubmittingCancelReview(true);
    try {
      await axios.put(`${API}/admin/cancellation-requests/${selectedCancelReq.id}/${cancelReviewAction}`, {
        notes: cancelReviewNotes,
      });
      toast.success(`Cancellation ${cancelReviewAction === 'approve' ? 'approved — order cancelled' : 'rejected — order restored'}`);
      setCancelReviewOpen(false);
      fetchCancelRequests();
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process request');
    } finally {
      setSubmittingCancelReview(false);
    }
  };

  // WebSocket handler for real-time updates
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[Orders WS] Received:', message);
    
    if (message.type === 'order_provisioned') {
      toast.success(`Order provisioned: ${message.data?.product_name || 'Service'} is now active`, {
        description: `Invoice ${message.data?.invoice_number || ''} generated`
      });
      fetchOrders();
    } else if (message.type === 'order_status_changed') {
      toast.info(`Order status changed: ${message.data?.old_status} → ${message.data?.new_status}`);
      fetchOrders();
    } else if (message.type === 'order_cancelled') {
      toast.warning(`Order cancelled: ${message.data?.product_name || 'Service'}`);
      fetchOrders();
    } else if (message.type === 'order_activated') {
      toast.success(`Order activated: ${message.data?.product_name || 'Service'}`);
      fetchOrders();
    } else if (message.type === 'order_created') {
      toast.info(`New order received: ${message.data?.product_name || 'Service'}`);
      fetchOrders();
    }
  }, [fetchOrders]);

  const { isConnected: wsConnected } = useOrderWebSocket(handleWebSocketMessage);

  useEffect(() => {
    fetchOrders();
    fetchCancelRequests();
    fetchServiceChangeReqs();
  }, [fetchOrders, fetchCancelRequests, fetchServiceChangeReqs]);

  // Lazy-load storefront requests when that tab is opened
  useEffect(() => {
    if (activeAdminTab === 'storefront') fetchWlRequests();
    if (activeAdminTab === 'self-orders') fetchSelfOrders();
  }, [activeAdminTab, fetchWlRequests, fetchSelfOrders]);

  // Handle status change with confirmation for suspend/cancel
  const onStatusDropdownChange = (orderId, newStatus) => {
    if (newStatus === 'suspended' || newStatus === 'cancelled') {
      // Show confirmation dialog for suspend/cancel
      setPendingStatusChange({ orderId, newStatus });
      setConfirmDialogOpen(true);
    } else {
      // Directly execute for other status changes
      executeStatusChange(orderId, newStatus);
    }
  };

  const executeStatusChange = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const confirmStatusChange = () => {
    if (pendingStatusChange.orderId && pendingStatusChange.newStatus) {
      executeStatusChange(pendingStatusChange.orderId, pendingStatusChange.newStatus);
    }
    setConfirmDialogOpen(false);
    setPendingStatusChange({ orderId: null, newStatus: null });
  };

  const cancelStatusChange = () => {
    setConfirmDialogOpen(false);
    setPendingStatusChange({ orderId: null, newStatus: null });
  };

  const handleDelete = async (orderId) => {
    if (!canDelete) {
      toast.error('Only Admin or Support Admin can delete orders');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`);
      toast.success('Order deleted');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  // Open provisioning dialog
  const openProvisionDialog = (order) => {
    setProvisioningOrder(order);
    setProvisioning({
      server_url: '',
      port: '',
      username: '',
      password: '',
      bios_ipmi_access: '',
      admin_notes: ''
    });
    setProvisionErrors({});
    setProvisionOpen(true);
  };

  // Submit provisioning
  const handleProvision = async () => {
    const errors = {};
    if (!provisioning.server_url?.trim()) errors.server_url = 'Server URL / IP is required';
    if (!provisioning.username?.trim()) errors.username = 'Username is required';
    if (!provisioning.password?.trim()) errors.password = 'Password is required';
    if (Object.keys(errors).length > 0) {
      setProvisionErrors(errors);
      return;
    }
    setProvisionErrors({});

    setSubmittingProvision(true);
    try {
      const payload = {
        ...provisioning,
        port: provisioning.port ? parseInt(provisioning.port) : null
      };
      
      const response = await axios.post(`${API}/admin/orders/${provisioningOrder.id}/provision`, payload);
      console.log('Provision response:', response.data);
      // Line 324 ke baad yeh add karo:
      setOrders(prev => prev.map(o =>
        o.id === provisioningOrder.id
          ? { ...o, ...response.data, provisioning_ready: true }
          : o
      ));
      if (response.data.status === 'payment_awaited') {
        toast.info(`Provisioning saved. Awaiting payment — invoice ${response.data.invoice_number} generated. Order will activate automatically once payment is received.`);
      } else {
        toast.success('Order provisioned and activated successfully!');
      }
      setProvisionOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to provision order');
    } finally {
      setSubmittingProvision(false);
    }
  };

  const viewOrderDetails = async (orderId) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderId}`);
      setSelectedOrder(response.data);
      setDetailsOpen(true);
    } catch (error) {
      toast.error('Failed to fetch order details');
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    const Icon = statusInfo.icon;
    return (
      <Badge className={`${statusInfo.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {statusInfo.label}
      </Badge>
    );
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesId = !orderIdSearch.trim() || order.id?.toLowerCase().includes(orderIdSearch.trim().toLowerCase());
    return matchesStatus && matchesId;
  });

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    processing: orders.filter(o => o.status === 'processing').length,
    active: orders.filter(o => o.status === 'active').length,
    suspended: orders.filter(o => o.status === 'suspended').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length
  };

  // Get allowed status options based on current status and permissions
  const getAllowedStatuses = (currentStatus) => {
    let allowed = VALID_TRANSITIONS[currentStatus] || [];
    
    // Filter out 'cancelled' if user doesn't have cancel permission
    if (!canCancelOrder) {
      allowed = allowed.filter(status => status !== 'cancelled');
    }
    
    return STATUS_OPTIONS.filter(opt => 
      opt.value === currentStatus || allowed.includes(opt.value)
    );
  };

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap md:nowrap">
          <div className="">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight" data-testid="orders-title">Orders Management</h1>
              {wsConnected ? (
                <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full" title="Real-time updates active">
                  <Wifi className="h-3 w-3" />
                  Live
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-full" title="Connecting...">
                  <WifiOff className="h-3 w-3" />
                  Offline
                </span>
              )}
            </div>
            <p className="text-slate-500 mt-1">View and manage all orders from resellers</p>
          </div>
          <Button variant="outline" onClick={fetchOrders} data-testid="refresh-orders">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveAdminTab('orders')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeAdminTab === 'orders' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="admin-tab-orders"
            >
              Orders
            </button>
            <button
              onClick={() => setActiveAdminTab('self-orders')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${activeAdminTab === 'self-orders' ? 'border-violet-600 text-violet-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="admin-tab-self-orders"
            >
              Partner Self-Orders
            </button>
            <button
              onClick={() => { setActiveAdminTab('service-changes'); fetchServiceChangeReqs(); }}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeAdminTab === 'service-changes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="admin-tab-service-changes"
            >
              Service Change Requests
              {serviceChangeReqs.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-2 bg-amber-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {serviceChangeReqs.filter(r => r.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveAdminTab('cancellations'); fetchCancelRequests(); }}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeAdminTab === 'cancellations' ? 'border-red-600 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="admin-tab-cancellations"
            >
              Cancellation Requests
              {cancelRequests.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {cancelRequests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveAdminTab('storefront')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${activeAdminTab === 'storefront' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="admin-tab-storefront"
            >
              <Globe className="h-3.5 w-3.5" />
              Storefront Requests
              {wlRequests.filter(r => r.status === 'New').length > 0 && (
                <span className="ml-1 bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {wlRequests.filter(r => r.status === 'New').length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Service Change Requests Tab */}
        {activeAdminTab === 'service-changes' && (
          <Card className="mt-2">
            <CardContent className="p-0">
              {scrLoading ? (
                <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
              ) : serviceChangeReqs.length === 0 ? (
                <div className="text-center py-16">
                  <ArrowUpDown className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No service change requests yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        {['Requester', 'Order / Service', 'Type', 'Requested Changes', 'Price', 'Date', 'Status', 'Actions'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-500 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {serviceChangeReqs.map(req => {
                        const typeColors = {
                          renewal: 'bg-blue-100 text-blue-700',
                          upgrade: 'bg-green-100 text-green-700',
                          downgrade: 'bg-orange-100 text-orange-700',
                        };
                        return (
                          <tr key={req.id} className="border-b last:border-0 hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-slate-800 whitespace-nowrap">{req.requester_company || req.requester_email || req.requester_id}</p>
                              <p className="text-xs text-slate-400 whitespace-nowrap">{req.requester_email}</p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-slate-700">{req.product_name || req.service_name || '—'}</p>
                              <p className="text-xs text-slate-400 font-mono">{req.order_id}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-0.5 rounded text-xs font-semibold capitalize ${typeColors[req.request_type] || 'bg-slate-100 text-slate-600'}`}>
                                {req.request_type || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 max-w-[180px]">
                              <p className="text-sm text-slate-600 line-clamp-2">{req.requested_changes || req.notes || '—'}</p>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {req.current_price != null && <p className="text-xs text-slate-400 line-through">₹{Number(req.current_price).toLocaleString('en-IN')}</p>}
                              {req.new_price != null && <p className="text-sm font-semibold text-slate-800">₹{Number(req.new_price).toLocaleString('en-IN')}</p>}
                              {req.current_price == null && req.new_price == null && <span className="text-slate-400">—</span>}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                              {new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-0.5 rounded text-xs font-medium ${req.status === 'approved' ? 'bg-green-100 text-green-700' : req.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                                {req.status?.charAt(0).toUpperCase() + req.status?.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1.5">
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => { setSelectedScr(req); setScrDetailOpen(true); }} data-testid={`view-scr-${req.id}`} title="View details">
                                  <Eye className="h-3 w-3" />
                                </Button>
                                {req.status === 'pending' && (<>
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-7 px-2 text-xs" onClick={() => handleScrApprove(req.id)} disabled={submittingScr} data-testid={`approve-scr-${req.id}`}>
                                    <Check className="h-3 w-3 mr-1" />Approve
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-7 px-2 text-xs" onClick={() => { setSelectedScr(req); setScrRejectReason(''); setScrRejectOpen(true); }} data-testid={`reject-scr-${req.id}`}>
                                    <X className="h-3 w-3 mr-1" />Reject
                                  </Button>
                                </>)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Cancellation Requests Tab */}
        {activeAdminTab === 'cancellations' && (
          <Card className="mt-2">
            <CardContent className="p-0">
              {cancelReqLoading ? (
                <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" /></div>
              ) : cancelRequests.length === 0 ? (
                <div className="text-center py-16">
                  <XCircle className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No cancellation requests yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        {['Client', 'Product / Service', 'Order ID', 'Reason', 'Submitted', 'Status', 'Action'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-500 text-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cancelRequests.map(req => (
                        <tr key={req.id} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-slate-800 text-nowrap">{req.client_company || req.client_email || '—'}</p>
                            <p className="text-xs text-slate-400 text-nowrap">{req.client_email}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 text-nowrap">{req.product_name || '—'}</td>
                          <td className="px-4 py-3 text-xs text-slate-500 font-mono text-nowrap">{req.order_id}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 max-w-xs">
                            <p className="truncate" title={req.reason}>{req.reason}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 text-nowrap">
                            {new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2.5 py-0.5 rounded text-xs font-medium ${req.status === 'approved' || req.status === 'processed' ? 'bg-green-100 text-green-700' : req.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                              {req.status === 'processed' ? 'Approved' : req.status?.charAt(0).toUpperCase() + req.status?.slice(1)}
                            </span>
                            {req.admin_notes && req.status !== 'pending' && (
                              <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[140px]" title={req.admin_notes}>{req.admin_notes}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {req.status === 'pending' ? (
                              <div className="flex gap-2">
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2" onClick={() => { setSelectedCancelReq(req); setCancelReviewAction('approve'); setCancelReviewNotes(''); setCancelReviewOpen(true); }} data-testid={`approve-cancel-${req.id}`}>
                                  <Check className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-600 hover:border-red-600 text-xs h-7 px-2" onClick={() => { setSelectedCancelReq(req); setCancelReviewAction('reject'); setCancelReviewNotes(''); setCancelReviewOpen(true); }} data-testid={`reject-cancel-${req.id}`}>
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">
                                {req.reviewed_at ? new Date(req.reviewed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'Reviewed'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Storefront Requests Tab */}
        {activeAdminTab === 'storefront' && (
          <div className="space-y-4 mt-2" data-testid="admin-storefront-requests-section">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-600" />
                  Storefront Requests
                </h2>
                <p className="text-sm text-slate-500">All order inquiries submitted via partner white-label storefronts</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchWlRequests} disabled={wlReqLoading} data-testid="admin-refresh-wl-requests">
                Refresh
              </Button>
            </div>

            {/* Partner filter */}
            {wlRequests.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-slate-600">Filter by Partner:</label>
                <Select value={wlPartnerFilter} onValueChange={setWlPartnerFilter}>
                  <SelectTrigger className="w-64 h-9" data-testid="admin-wl-partner-filter">
                    <SelectValue placeholder="All Partners" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Partners</SelectItem>
                    {[...new Map(wlRequests.map(r => [r.partner_id, r.partner_company_name])).entries()].map(([pid, name]) => (
                      <SelectItem key={pid} value={pid}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {wlReqLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : wlRequests.length === 0 ? (
              <Card>
                <CardContent className="text-center py-16">
                  <Globe className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No storefront requests yet</p>
                  <p className="text-sm text-slate-400 mt-1">Requests will appear here once visitors submit inquiries via partner storefronts</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Partner</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Visitor Name</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Mobile</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Email</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Plan/Service</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {wlRequests
                        .filter(r => wlPartnerFilter === 'all' || r.partner_id === wlPartnerFilter)
                        .map(req => (
                        <tr key={req.id} className="hover:bg-slate-50" data-testid={`admin-wl-req-row-${req.id}`}>
                          <td className="px-4 py-3 font-medium text-blue-700 text-xs">{req.partner_company_name}</td>
                          <td className="px-4 py-3 font-medium text-slate-800">{req.visitor_name}</td>
                          <td className="px-4 py-3 text-slate-600">{req.visitor_mobile}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{req.visitor_email}</td>
                          <td className="px-4 py-3 text-slate-700 max-w-[160px] truncate">{req.product_name}</td>
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                            {new Date(req.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                          </td>
                          <td className="px-4 py-3">
                            <Select
                              value={req.status}
                              onValueChange={val => handleWlStatusChange(req.id, val)}
                              disabled={wlUpdating === req.id}
                            >
                              <SelectTrigger className="h-8 w-32 text-xs" data-testid={`admin-wl-status-${req.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="New">New</SelectItem>
                                <SelectItem value="Contacted">Contacted</SelectItem>
                                <SelectItem value="Converted">Converted</SelectItem>
                                <SelectItem value="Closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Partner Self-Orders Tab */}
        {activeAdminTab === 'self-orders' && (
          <div className="space-y-4 mt-2" data-testid="admin-self-orders-section">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Partner Self-Orders</h2>
                <p className="text-sm text-slate-500">Orders placed by partners for their own use</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={selfOrderStatusFilter} onValueChange={setSelfOrderStatusFilter}>
                  <SelectTrigger className="w-40 h-9 text-sm" data-testid="self-order-status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchSelfOrders} disabled={selfOrdersLoading} data-testid="refresh-self-orders">
                  Refresh
                </Button>
              </div>
            </div>
            {selfOrdersLoading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" /></div>
            ) : selfOrders.filter(o => selfOrderStatusFilter === 'all' || o.status === selfOrderStatusFilter).length === 0 ? (
              <Card><CardContent className="text-center py-16">
                <p className="text-slate-500 font-medium">No partner self-orders found</p>
              </CardContent></Card>
            ) : (
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Partner</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Product</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Cycle</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Amount</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Renewal Date</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selfOrders.filter(o => selfOrderStatusFilter === 'all' || o.status === selfOrderStatusFilter).map(so => (
                        <tr key={so.id} className="hover:bg-slate-50" data-testid={`self-order-row-${so.id}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-800">{so.partner_company_name || 'Partner'}</p>
                            <p className="text-xs text-slate-400">{so.partner_email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-700">{so.product_name}</p>
                            {so.notes && <p className="text-xs text-slate-400 truncate max-w-[180px]">{so.notes}</p>}
                          </td>
                          <td className="px-4 py-3 text-slate-600 capitalize">{so.billing_cycle || '—'}</td>
                          <td className="px-4 py-3 text-right font-medium text-slate-800">
                            ₹{(so.total_amount || so.subtotal || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              so.status === 'active' ? 'bg-green-100 text-green-700' :
                              so.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              so.status === 'pending_payment' ? 'bg-orange-100 text-orange-800' :
                              so.status === 'expired' ? 'bg-orange-100 text-orange-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {so.status === 'pending_payment' ? 'Pending Payment' : so.status?.charAt(0).toUpperCase() + so.status?.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {so.renewal_date ? new Date(so.renewal_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              {(so.status === 'pending' || so.status === 'pending_payment') && (
                                <Button
                                  size="sm"
                                  className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-7"
                                  onClick={() => { setSelfOrderProvisioningOrder(so); setSelfOrderProvisionData({ server_url: so.provisioning?.server_url || '', port: so.provisioning?.port || '', username: so.provisioning?.username || '', password: so.provisioning?.password || '', bios_ipmi_access: so.provisioning?.bios_ipmi_access || '', admin_notes: so.admin_notes || '' }); setSelfOrderProvisionErrors({}); setSelfOrderProvisionOpen(true); }}
                                  data-testid={`provision-self-order-${so.id}`}
                                >
                                  {so.status === 'pending_payment' ? 'Update' : 'Provision'}
                                </Button>
                              )}
                              {(so.provisioning || so.status === 'active' || so.status === 'pending_payment') && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 w-7 p-0 border-slate-200"
                                  onClick={() => { setSelfOrderDetail(so); setSelfOrderDetailOpen(true); }}
                                  data-testid={`view-self-order-${so.id}`}
                                  title="View provisioning details"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeAdminTab === 'orders' && (<>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4 mt-2">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Orders</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <ShoppingCart className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`} onClick={() => setStatusFilter('pending')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'processing' ? 'ring-2 ring-blue-500' : ''}`} onClick={() => setStatusFilter('processing')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Processing</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.processing}</p>
                </div>
                <Play className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'active' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setStatusFilter('active')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'suspended' ? 'ring-2 ring-orange-500' : ''}`} onClick={() => setStatusFilter('suspended')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Suspended</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.suspended}</p>
                </div>
                <Pause className="h-8 w-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'cancelled' ? 'ring-2 ring-red-500' : ''}`} onClick={() => setStatusFilter('cancelled')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Cancelled</p>
                  <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex gap-4 items-center py-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              {STATUS_OPTIONS.map(status => (
                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by Order ID..."
              value={orderIdSearch}
              onChange={e => setOrderIdSearch(e.target.value)}
              className="pl-9 w-auto md:w-64 !text-sm md:!text-base"
              data-testid="order-id-search"
            />
          </div>
          <span className="text-sm text-slate-500">Showing {filteredOrders.length} orders</span>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ShoppingCart className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No orders found</h3>
              <p className="text-slate-500 text-sm mt-1">Orders will appear here when resellers finalize quotations</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 grid gap-4 overflow-x-auto">
            {filteredOrders.map(order => (
              <Card key={order.id} data-testid={`order-${order.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between flex-wrap md:flex-nowrap">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{order.product_name}</h3>
                        {getStatusBadge(order.status)}
                        {order.source === 'client_request' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 border border-violet-200" data-testid={`source-badge-${order.id}`}>
                            Client Request
                          </span>
                        )}
                        {order.payment_status === 'invoice_pending' && order.provisioning_ready && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200" data-testid={`payment-status-${order.id}`}>
                            Payment Awaited
                          </span>
                        )}
                        {order.payment_status === 'invoice_paid' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200" data-testid={`payment-status-${order.id}`}>
                            Invoice Paid
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-600 grid grid-cols-2 gap-x-8 gap-y-1">
                        <p><span className="text-slate-500">Order ID:</span> {order.id}</p>
                        <p><span className="text-slate-500">Billing:</span> {order.billing_cycle?.replace('_', ' ')}</p>
                        <p><span className="text-slate-500">Reseller:</span> {order.reseller?.company_name || 'N/A'}</p>
                        <p><span className="text-slate-500">Client:</span> {order.client?.company_name || 'N/A'}</p>
                        <p><span className="text-slate-500">Qty:</span> {order.quantity || 1}</p>
                        <p><span className="text-slate-500">Created:</span> {new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right space-y-3 w-full md:w-auto mt-3 md:mt-0">
                      <div>
                        <p className="text-2xl font-bold text-blue-600 mono">₹{(order.display_amount ?? order.amount)?.toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2 items-center">
                        {canEditOrder && (
                          <Select 
                            value={order.status} 
                            onValueChange={(value) => onStatusDropdownChange(order.id, value)}
                            disabled={updating === order.id}
                          >
                            <SelectTrigger className="w-32" data-testid={`status-select-${order.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getAllowedStatuses(order.status).map(status => (
                                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {!canEditOrder && (
                          <span className="text-sm text-slate-500">{order.status}</span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewOrderDetails(order.id)}
                          data-testid={`view-${order.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canDeleteOrder && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(order.id)}
                            data-testid={`delete-${order.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                        {/* Provision button - only for processing orders and users with provision permission */}
                        {order.status === 'processing' && canProvisionOrder && (
                          <Button
                            size="sm"
                            className={`text-white ${order.payment_status === 'invoice_pending' && order.provisioning_ready ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-600 hover:bg-green-700'}`}
                            onClick={() => openProvisionDialog(order)}
                            title={order.payment_status === 'invoice_pending' && order.provisioning_ready ? 'Provisioning saved — order will activate once payment is received' : 'Fill provisioning details and activate this order'}
                            data-testid={`provision-${order.id}`}
                          >
                            <Server className="h-4 w-4 mr-1" />
                            {order.payment_status === 'invoice_pending' && order.provisioning_ready ? 'Provision (Awaiting Payment)' : 'Provision'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        </> )} {/* end activeAdminTab === 'orders' */}

        {/* Order Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Order Details</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Order ID</p>
                    <p className="font-medium">{selectedOrder.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    {getStatusBadge(selectedOrder.status)}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Product</p>
                    <p className="font-medium">{selectedOrder.product_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Amount</p>
                    <p className="font-medium text-blue-600">₹{(selectedOrder.display_amount ?? selectedOrder.amount)?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Quantity</p>
                    <p className="font-medium">{selectedOrder.quantity || 1}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Billing Cycle</p>
                    <p className="font-medium">{selectedOrder.billing_cycle?.replace('_', ' ')}</p>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Reseller</p>
                  <div className="bg-[#2563eb]/10 p-3 rounded-lg text-sm">
                    <p><span className="text-slate-500">Company:</span> {selectedOrder.reseller?.company_name}</p>
                    <p><span className="text-slate-500">Email:</span> {selectedOrder.reseller?.email}</p>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Client</p>
                  <div className="bg-[#2563eb]/10 p-3 rounded-lg text-sm">
                    <p><span className="text-slate-500">Company:</span> {selectedOrder.client?.company_name}</p>
                    <p><span className="text-slate-500">Email:</span> {selectedOrder.client?.email}</p>
                  </div>
                </div>

                {selectedOrder.notes && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2">Notes</p>
                    <p className="text-sm text-slate-600">{selectedOrder.notes}</p>
                  </div>
                )}
                {selectedOrder.provisioning && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2 flex items-center gap-1">
                      <Server className="h-4 w-4 text-green-600" /> Provisioning Details
                    </p>
                    <div className="bg-green-50 border border-green-200 p-3 rounded-lg text-sm space-y-1">
                      <p><span className="text-slate-500">Server URL:</span> {selectedOrder.provisioning.server_url}</p>
                      <p><span className="text-slate-500">Port:</span> {selectedOrder.provisioning.port || '—'}</p>
                      <p><span className="text-slate-500">Username:</span> {selectedOrder.provisioning.username}</p>
                      <p><span className="text-slate-500">BIOS/IPMI:</span> {selectedOrder.provisioning.bios_ipmi_access || '—'}</p>
                      {selectedOrder.provisioning.admin_notes && (
                        <p><span className="text-slate-500">Notes:</span> {selectedOrder.provisioning.admin_notes}</p>
                      )}
                    </div>
                  </div>
                )}
                <div className="text-xs text-slate-500 border-t pt-4">
                  <p>Created: {new Date(selectedOrder.created_at).toLocaleString()}</p>
                  {selectedOrder.expires_at && (
                    <p>Expires: {new Date(selectedOrder.expires_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Provisioning Dialog */}
        <Dialog open={provisionOpen} onOpenChange={setProvisionOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Server className="h-5 w-5 text-green-600" />
                Provision Order
              </DialogTitle>
            </DialogHeader>
            {provisioningOrder && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800">{provisioningOrder.product_name}</p>
                  <p className="text-blue-600">Order: {provisioningOrder.id.slice(0, 8)}... | Client: {provisioningOrder.client?.company_name || 'N/A'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label className="flex items-center gap-1">
                      Server URL / IP <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="e.g., 192.168.1.100 or server.example.com"
                      value={provisioning.server_url}
                      onChange={(e) => { setProvisioning(prev => ({ ...prev, server_url: e.target.value })); if (provisionErrors.server_url) setProvisionErrors(prev => ({ ...prev, server_url: '' })); }}
                      className={provisionErrors.server_url ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      required
                      data-testid="provision-server-url"
                    />
                    {provisionErrors.server_url && <p className="text-xs text-red-500 mt-1">{provisionErrors.server_url}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">Port Number</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 22, 3389"
                      value={provisioning.port}
                      onChange={(e) => setProvisioning(prev => ({ ...prev, port: e.target.value }))}
                      data-testid="provision-port"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Key className="h-3 w-3" /> Username <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="Username"
                      value={provisioning.username}
                      onChange={(e) => { setProvisioning(prev => ({ ...prev, username: e.target.value })); if (provisionErrors.username) setProvisionErrors(prev => ({ ...prev, username: '' })); }}
                      className={provisionErrors.username ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      required
                      data-testid="provision-username"
                    />
                    {provisionErrors.username && <p className="text-xs text-red-500 mt-1">{provisionErrors.username}</p>}
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label className="flex items-center gap-1">
                      <Key className="h-3 w-3" /> Password <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="password"
                      placeholder="Password"
                      value={provisioning.password}
                      onChange={(e) => { setProvisioning(prev => ({ ...prev, password: e.target.value })); if (provisionErrors.password) setProvisionErrors(prev => ({ ...prev, password: '' })); }}
                      className={provisionErrors.password ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      required
                      data-testid="provision-password"
                    />
                    {provisionErrors.password && <p className="text-xs text-red-500 mt-1">{provisionErrors.password}</p>}
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label>BIOS / IPMI Access</Label>
                    <Input
                      placeholder="e.g., https://ipmi.server.com or IPMI credentials"
                      value={provisioning.bios_ipmi_access}
                      onChange={(e) => setProvisioning(prev => ({ ...prev, bios_ipmi_access: e.target.value }))}
                      data-testid="provision-bios"
                    />
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label>Admin Notes</Label>
                    <Textarea
                      placeholder="Any additional notes for the client..."
                      value={provisioning.admin_notes}
                      onChange={(e) => setProvisioning(prev => ({ ...prev, admin_notes: e.target.value }))}
                      rows={3}
                      data-testid="provision-notes"
                    />
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <p className="font-medium flex items-center gap-1">
                    <FileText className="h-4 w-4" /> On Submit:
                  </p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Credentials will be sent to Client & Partner dashboards</li>
                    <li>Wallet balance will be checked <span className="font-medium">at this point</span></li>
                    <li>If balance is sufficient → Order activates immediately</li>
                    <li>If insufficient → Order moves to <span className="font-medium">Payment Awaited</span> and activates automatically once payment is received</li>
                  </ul>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setProvisionOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleProvision}
                disabled={submittingProvision}
                data-testid="submit-provision"
              >
                {submittingProvision ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit & Activate
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancellation Review Dialog */}
        <Dialog open={cancelReviewOpen} onOpenChange={setCancelReviewOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{cancelReviewAction === 'approve' ? 'Approve Cancellation' : 'Reject Cancellation'}</DialogTitle>
            </DialogHeader>
            {selectedCancelReq && (
              <div className="space-y-3">
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <p className="font-medium text-slate-800">{selectedCancelReq.product_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Client: {selectedCancelReq.client_company || selectedCancelReq.client_email}</p>
                  <p className="text-xs text-slate-500 mt-1"><span className="font-medium">Reason:</span> {selectedCancelReq.reason}</p>
                </div>
                {cancelReviewAction === 'approve' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    Approving will immediately <strong>cancel this order</strong> and notify the client.
                  </div>
                )}
                {cancelReviewAction === 'reject' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    Rejecting will restore the order to <strong>Active</strong> status and notify the client.
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Admin Notes (optional)</label>
                  <textarea
                    className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={cancelReviewNotes}
                    onChange={e => setCancelReviewNotes(e.target.value)}
                    placeholder="Add a note for the client (optional)..."
                    data-testid="cancel-review-notes"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelReviewOpen(false)}>Cancel</Button>
              <Button
                className={cancelReviewAction === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                onClick={submitCancelReview}
                disabled={submittingCancelReview}
                data-testid="submit-cancel-review-btn"
              >
                {submittingCancelReview && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />}
                {cancelReviewAction === 'approve' ? 'Approve & Cancel Order' : 'Reject Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Suspend/Cancel Confirmation Dialog */}
        <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="status-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" data-testid="status-confirm-title">
                {pendingStatusChange.newStatus === 'suspended' ? (
                  <Pause className="h-5 w-5 text-orange-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                Confirm {pendingStatusChange.newStatus === 'suspended' ? 'Suspend' : 'Cancel'} Order
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600" data-testid="status-confirm-message">
                Are you sure you want to <strong className={pendingStatusChange.newStatus === 'suspended' ? 'text-orange-600' : 'text-red-600'}>
                  {pendingStatusChange.newStatus === 'suspended' ? 'Suspend' : 'Cancel'}
                </strong> this order?
              </p>
              {pendingStatusChange.newStatus === 'cancelled' && (
                <p className="text-sm text-slate-500 mt-2">
                  This action may trigger a wallet refund if the order was already paid.
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={cancelStatusChange} data-testid="confirm-no">
                No
              </Button>
              <Button 
                variant={pendingStatusChange.newStatus === 'suspended' ? 'default' : 'destructive'}
                onClick={confirmStatusChange}
                data-testid="confirm-yes"
                className={pendingStatusChange.newStatus === 'suspended' ? 'bg-orange-600 hover:bg-orange-700' : ''}
              >
                Yes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* SCR Detail Dialog */}
      <Dialog open={scrDetailOpen} onOpenChange={setScrDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Service Change Request Details</DialogTitle></DialogHeader>
          {selectedScr && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-slate-400">Requester</p><p className="font-medium">{selectedScr.requester_company || selectedScr.requester_email}</p></div>
                <div><p className="text-xs text-slate-400">Email</p><p className="font-medium">{selectedScr.requester_email}</p></div>
                <div><p className="text-xs text-slate-400">Product / Service</p><p className="font-medium">{selectedScr.product_name || selectedScr.service_name || '—'}</p></div>
                <div><p className="text-xs text-slate-400">Request Type</p>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${selectedScr.request_type === 'upgrade' ? 'bg-green-100 text-green-700' : selectedScr.request_type === 'downgrade' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                    {selectedScr.request_type}
                  </span>
                </div>
                {selectedScr.current_price != null && <div><p className="text-xs text-slate-400">Current Price</p><p className="font-medium">₹{Number(selectedScr.current_price).toLocaleString('en-IN')}</p></div>}
                {selectedScr.new_price != null && <div><p className="text-xs text-slate-400">Requested Price</p><p className="font-semibold text-blue-600">₹{Number(selectedScr.new_price).toLocaleString('en-IN')}</p></div>}
              </div>
              {selectedScr.requested_changes && <div><p className="text-xs text-slate-400 mb-1">Requested Changes</p><p className="bg-slate-50 rounded p-2">{selectedScr.requested_changes}</p></div>}
              {selectedScr.notes && <div><p className="text-xs text-slate-400 mb-1">Notes</p><p className="bg-slate-50 rounded p-2">{selectedScr.notes}</p></div>}
              {selectedScr.rejection_reason && <div><p className="text-xs text-slate-400 mb-1">Rejection Reason</p><p className="bg-red-50 rounded p-2 text-red-700">{selectedScr.rejection_reason}</p></div>}
            </div>
          )}
          <DialogFooter className="gap-2">
            {selectedScr?.status === 'pending' && (<>
              <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { handleScrApprove(selectedScr.id); setScrDetailOpen(false); }} disabled={submittingScr}>
                <Check className="h-4 w-4 mr-1" />Approve
              </Button>
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setScrRejectReason(''); setScrRejectOpen(true); }}>
                <X className="h-4 w-4 mr-1" />Reject
              </Button>
            </>)}
            <Button variant="outline" onClick={() => setScrDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SCR Reject Dialog */}
      <Dialog open={scrRejectOpen} onOpenChange={setScrRejectOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reject Service Change Request</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Rejection reason (optional)</Label>
            <textarea className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 min-h-[80px] resize-none" value={scrRejectReason} onChange={e => setScrRejectReason(e.target.value)} placeholder="Let the requester know why..." data-testid="scr-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScrRejectOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleScrReject} disabled={submittingScr} data-testid="submit-scr-reject">
              {submittingScr ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <X className="h-4 w-4 mr-2" />}
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Self-Order Provision Dialog */}
      <Dialog open={selfOrderProvisionOpen} onOpenChange={(o) => { if (!o) { setSelfOrderProvisionOpen(false); setSelfOrderProvisioningOrder(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Provision Self-Order</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              {selfOrderProvisioningOrder?.product_name} — {selfOrderProvisioningOrder?.partner_company_name}
            </p>
          </DialogHeader>
          {selfOrderProvisioningOrder && (
            <div className="space-y-3 pt-1">
              {[
                { label: 'Server URL *', key: 'server_url', placeholder: 'https://server.example.com' },
                { label: 'Port', key: 'port', placeholder: '22' },
                { label: 'Username *', key: 'username', placeholder: 'root' },
                { label: 'Password *', key: 'password', placeholder: 'password' },
                { label: 'BIOS / IPMI Access', key: 'bios_ipmi_access', placeholder: 'Optional' },
              ].map(({ label, key, placeholder }) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs font-medium text-slate-600">{label}</label>
                  <input
                    className={`w-full border rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500 ${selfOrderProvisionErrors[key] ? 'border-red-400' : 'border-slate-200'}`}
                    placeholder={placeholder}
                    value={selfOrderProvisionData[key]}
                    onChange={e => setSelfOrderProvisionData(prev => ({ ...prev, [key]: e.target.value }))}
                    data-testid={`self-order-provision-${key}`}
                  />
                  {selfOrderProvisionErrors[key] && <p className="text-xs text-red-500">{selfOrderProvisionErrors[key]}</p>}
                </div>
              ))}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Renewal Date</label>
                <input
                  type="date"
                  className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  value={selfOrderProvisionData.renewal_date || ''}
                  onChange={e => setSelfOrderProvisionData(prev => ({ ...prev, renewal_date: e.target.value }))}
                  data-testid="self-order-provision-renewal-date"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Admin Notes</label>
                <textarea
                  className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
                  rows={2}
                  placeholder="Optional notes for this provision"
                  value={selfOrderProvisionData.admin_notes}
                  onChange={e => setSelfOrderProvisionData(prev => ({ ...prev, admin_notes: e.target.value }))}
                  data-testid="self-order-provision-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelfOrderProvisionOpen(false); setSelfOrderProvisioningOrder(null); }}>Cancel</Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700 text-white"
              disabled={submittingSelfProvision}
              onClick={async () => {
                const errs = {};
                if (!selfOrderProvisionData.server_url.trim()) errs.server_url = 'Required';
                if (!selfOrderProvisionData.username.trim()) errs.username = 'Required';
                if (!selfOrderProvisionData.password.trim()) errs.password = 'Required';
                if (Object.keys(errs).length) { setSelfOrderProvisionErrors(errs); return; }
                setSubmittingSelfProvision(true);
                try {
                  const res = await axios.post(`${API}/admin/self-orders/${selfOrderProvisioningOrder.id}/provision`, {
                    ...selfOrderProvisionData,
                    port: selfOrderProvisionData.port ? parseInt(selfOrderProvisionData.port) : null,
                  });
                  const newStatus = res.data?.status;
                  if (newStatus === 'pending_payment') {
                    toast.warning('Provisioning details saved. Order marked as Pending Payment — will auto-activate when partner tops up wallet.');
                  } else {
                    toast.success('Self-order provisioned and activated. Wallet debited.');
                  }
                  setSelfOrderProvisionOpen(false);
                  setSelfOrderProvisioningOrder(null);
                  fetchSelfOrders();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to provision self-order');
                } finally {
                  setSubmittingSelfProvision(false);
                }
              }}
              data-testid="submit-self-order-provision"
            >
              {submittingSelfProvision ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : null}
              Provision &amp; Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Self-Order Detail Modal */}
      <Dialog open={selfOrderDetailOpen} onOpenChange={o => { if (!o) setSelfOrderDetailOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-violet-500" /> Order Provisioning Details
            </DialogTitle>
          </DialogHeader>
          {selfOrderDetail && (
            <div className="space-y-4 py-1">
              <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                <p className="font-semibold text-slate-800">{selfOrderDetail.product_name}</p>
                <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                  <span>{selfOrderDetail.partner_name || selfOrderDetail.partner_email}</span>
                  <span>•</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    selfOrderDetail.status === 'active' ? 'bg-green-100 text-green-700' :
                    selfOrderDetail.status === 'pending_payment' ? 'bg-orange-100 text-orange-800' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {selfOrderDetail.status === 'pending_payment' ? 'Pending Payment' : selfOrderDetail.status}
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-700 mt-1">₹{(selfOrderDetail.total_amount || 0).toLocaleString('en-IN')}</p>
              </div>
              {selfOrderDetail.provisioning ? (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Provisioning Details</p>
                  {[
                    { label: 'Server URL', value: selfOrderDetail.provisioning.server_url },
                    { label: 'Port', value: selfOrderDetail.provisioning.port },
                    { label: 'Username', value: selfOrderDetail.provisioning.username },
                    { label: 'Password', value: selfOrderDetail.provisioning.password, mono: true },
                    { label: 'BIOS / IPMI', value: selfOrderDetail.provisioning.bios_ipmi_access },
                  ].filter(f => f.value).map(f => (
                    <div key={f.label} className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-100">
                      <span className="text-xs text-slate-500 w-24 flex-shrink-0">{f.label}</span>
                      <code className={`text-sm flex-1 ${f.mono ? 'font-bold text-slate-800' : 'text-slate-700'}`}>{f.value}</code>
                    </div>
                  ))}
                  <p className="text-xs text-slate-400">
                    Provisioned: {new Date(selfOrderDetail.provisioning.provisioned_at).toLocaleString('en-IN')}
                  </p>
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400 text-sm">
                  No provisioning details available yet.
                </div>
              )}
              {selfOrderDetail.admin_notes && (
                <div className="p-3 bg-amber-50 rounded border border-amber-100">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Admin Notes</p>
                  <p className="text-sm text-amber-800">{selfOrderDetail.admin_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
